import { Fragment, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";

import { segmentWerte, zieheGrenze, type BalkenZeile, type FeldWert, type VorschauBaustein } from "@repo/shared";

import { zahlText } from "./FeldRahmen";

type Bearbeitbar = NonNullable<BalkenZeile["bearbeitbar"]>;

const summe = (werte: number[]): number => werte.reduce((a, b) => a + b, 0);

const prozent = (wert: number, gesamt: number): string => `${gesamt > 0 ? Math.max(0, (wert / gesamt) * 100) : 0}%`;

/**
 * Ein Balken aus Segmenten. Ist die Zeile bearbeitbar, sitzt auf jeder Grenze
 * ein Griff: Ziehen setzt das Segment links davon (Raster, Minimum, der Rest
 * geht ans letzte Segment) und meldet den Wert als Feldänderung.
 */
function Balken({
  zeile,
  werte,
  onChange,
}: {
  zeile: BalkenZeile;
  werte: Record<string, FeldWert>;
  onChange: (schluessel: string, wert: FeldWert) => void;
}) {
  const spur = useRef<HTMLDivElement>(null);
  const [ziehIndex, setZiehIndex] = useState<number | null>(null);
  /** Stand während des Ziehens – nur in Ereignis-Handlern geschrieben, nie im Render. */
  const zieht = useRef<{ index: number; werte: number[] } | null>(null);

  // Die Feldwerte ändern sich bei `onChange` sofort, ein eigener Zwischenstand ist nicht nötig.
  const anzeige = segmentWerte(zeile, werte);
  const b = zeile.bearbeitbar;

  function setze(index: number, position: number, vorher: number[], bearbeitbar: Bearbeitbar): number[] | null {
    const neu = zieheGrenze(vorher, index, position, bearbeitbar.raster, bearbeitbar.min);
    if (neu[index] === vorher[index]) return null;
    onChange(bearbeitbar.felder[index]!, Math.round(neu[index]! * bearbeitbar.feldFaktor));
    return neu;
  }

  function beginnen(index: number, e: PointerEvent<HTMLButtonElement>) {
    e.currentTarget.setPointerCapture(e.pointerId);
    zieht.current = { index, werte: anzeige };
    setZiehIndex(index);
  }

  function ziehen(index: number, e: PointerEvent<HTMLButtonElement>, bearbeitbar: Bearbeitbar) {
    const lauf = zieht.current;
    if (!lauf || lauf.index !== index || !spur.current) return;
    const rect = spur.current.getBoundingClientRect();
    if (rect.width <= 0) return;
    const neu = setze(index, ((e.clientX - rect.left) / rect.width) * zeile.gesamt, lauf.werte, bearbeitbar);
    if (neu) zieht.current = { index, werte: neu };
  }

  function loslassen() {
    zieht.current = null;
    setZiehIndex(null);
  }

  function taste(index: number, e: KeyboardEvent<HTMLButtonElement>, bearbeitbar: Bearbeitbar) {
    const richtung = e.key === "ArrowLeft" ? -1 : e.key === "ArrowRight" ? 1 : 0;
    if (richtung === 0) return;
    e.preventDefault();
    const grenze = summe(anzeige.slice(0, index + 1));
    setze(index, grenze + richtung * bearbeitbar.raster, anzeige, bearbeitbar);
  }

  /** Größtmögliche Breite von Segment `index`: der Rest behält das Minimum (wie in `zieheGrenze`). */
  function hoechstbreite(index: number, bearbeitbar: Bearbeitbar): number {
    const vorher = summe(anzeige.slice(0, index));
    const zwischen = summe(anzeige.slice(index + 1, -1));
    return Math.floor((summe(anzeige) - vorher - zwischen - bearbeitbar.min) / bearbeitbar.raster) * bearbeitbar.raster;
  }

  const grenzen = anzeige.slice(0, -1).map((_, i) => summe(anzeige.slice(0, i + 1)));

  return (
    <div className="konfi-balken">
      <div className="konfi-balken__kopf">{zeile.label}</div>
      <div ref={spur} className={`konfi-balken__spur konfi-balken__spur--${zeile.stil}`}>
        {anzeige.map((wert, i) => (
          <div key={i} className="konfi-balken__segment" style={{ width: prozent(wert, zeile.gesamt) }}>
            <strong>
              {zahlText(wert)} {zeile.einheit}
            </strong>
            <span>{zeile.segmente[i]?.label}</span>
          </div>
        ))}
        {b &&
          grenzen.map((grenze, i) =>
            i < b.felder.length ? (
              <button
                key={i}
                type="button"
                className={ziehIndex === i ? "konfi-griff konfi-griff--aktiv" : "konfi-griff"}
                style={{ left: prozent(grenze, zeile.gesamt) }}
                role="slider"
                aria-orientation="horizontal"
                aria-valuemin={b.min}
                aria-valuemax={hoechstbreite(i, b)}
                aria-valuenow={anzeige[i]}
                aria-valuetext={`${zahlText(anzeige[i] ?? 0)} ${zeile.einheit}`}
                aria-label={`Grenze nach ${zeile.segmente[i]?.label ?? `Segment ${i + 1}`} verschieben`}
                title={`Ziehen oder Pfeiltasten – ${zahlText(b.raster)}-${zeile.einheit}-Raster, mindestens ${zahlText(b.min)} ${zeile.einheit}`}
                onPointerDown={(e) => beginnen(i, e)}
                onPointerMove={(e) => ziehen(i, e, b)}
                onPointerUp={loslassen}
                onPointerCancel={loslassen}
                onKeyDown={(e) => taste(i, e, b)}
              />
            ) : null,
          )}
      </div>
    </div>
  );
}

/** Kennzahlen, Tabellen und Balken aus `ergebnis.vorschau` – nur Geometrie und Mengen, nie Preise. */
export function VorschauBausteine({
  bausteine,
  werte,
  onChange,
}: {
  bausteine: VorschauBaustein[];
  werte: Record<string, FeldWert>;
  onChange: (schluessel: string, wert: FeldWert) => void;
}) {
  if (bausteine.length === 0) return null;
  return (
    <div className="konfi-vorschau">
      {bausteine.map((baustein) => (
        <section key={baustein.schluessel} className={`konfi-baustein konfi-baustein--${baustein.art}`}>
          <h3>{baustein.titel}</h3>

          {baustein.art === "kennzahlen" && (
            <dl className="konfi-kennzahlen">
              {baustein.eintraege.map((e, i) => (
                <div key={`${i}-${e.label}`} className={`konfi-kennzahl konfi-ton--${e.ton}`}>
                  <dt>{e.label}</dt>
                  <dd>{e.wert}</dd>
                </div>
              ))}
            </dl>
          )}

          {baustein.art === "tabelle" && (
            <>
              <div className="konfi-baustein__rahmen">
                <table className="table table--kompakt konfi-vorschau-tabelle">
                  <thead>
                    <tr>
                      {baustein.spalten.map((s, i) => (
                        <th key={i} className={s.ausrichtung === "rechts" ? "num" : undefined}>
                          {s.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {baustein.zeilen.map((zeile, z) => (
                      <tr key={z}>
                        {zeile.map((zelle, i) => (
                          <td key={i} className={baustein.spalten[i]?.ausrichtung === "rechts" ? "num" : undefined}>
                            {zelle}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {baustein.fussnote && <p className="muted small konfi-baustein__fussnote">{baustein.fussnote}</p>}
            </>
          )}

          {baustein.art === "balken" &&
            baustein.zeilen.map((zeile, z) => (
              <Fragment key={z}>
                {z > 0 && baustein.zwischen && (
                  <div className="konfi-balken__zwischen">
                    <span>{baustein.zwischen}</span>
                  </div>
                )}
                <Balken zeile={zeile} werte={werte} onChange={onChange} />
              </Fragment>
            ))}

          {baustein.art === "balken" && baustein.zeilen.some((z) => z.bearbeitbar) && (
            <p className="muted small konfi-baustein__fussnote">
              Griffe ziehen oder mit den Pfeiltasten verschieben – das letzte Segment erhält den Rest.
            </p>
          )}
        </section>
      ))}
    </div>
  );
}
